import type { RecordSkillOutput } from './record-skill';
import type {
  CustomGraph,
  GraphIssue,
  HoldChoice,
  TriggerHealth,
  TriggerKind,
  TriggerSchedule,
} from '@/types/custom-playbooks';

/**
 * Wire contract for the agent and skill records: the backend routers at
 * cleanse-api-v2 `cleanse_api/api/agents/gtm_agents.py` (/api/v1/gtm-agents)
 * and `skills.py` (/api/v1/skills). Field names are the API's own camelCase,
 * one for one with the DTOs, no transform layer.
 */

export type AgentStatus = 'draft' | 'active' | 'paused';

/** The sixteen tool switches an agent carries, in the backend's vocabulary. */
export type ToolKey =
  | 'find_people'
  | 'find_companies'
  | 'enrich'
  | 'validate'
  | 'record_skills'
  | 'lists'
  | 'crm_read'
  | 'crm_write'
  | 'slack_post'
  | 'send_email'
  | 'web_search'
  | 'ask_user'
  | 'run_play'
  | 'manage_triggers'
  | 'edit_instructions'
  | 'memory';

/**
 * Per tool on a connection: runs on its own, asks first, or never runs (off
 * for that connection only; the agent wide `tools` switch stays as it is).
 */
export type ToolPolicy = 'allow' | 'ask' | 'never';

export type SkillKind = 'procedure' | 'record';

/** An attached skill as a PATCH carries it. */
export interface SkillRefInput {
  kind: SkillKind;
  /** A procedure skill's id, or `builtin:research`, `builtin:qualification`, `custom:<uuid>`. */
  id: string;
}

/** An attached skill as the agent returns it, with its name resolved. */
export interface SkillRef extends SkillRefInput {
  name: string;
}

export interface AgentKnowledge {
  profile: boolean;
  companyNotes: boolean;
}

export type ConnectionType =
  | 'salesforce'
  | 'hubspot'
  | 'attio'
  | 'slack'
  | 'email';

/** Every connection type, in the order the API lists connections. */
export const CONNECTION_TYPES: readonly ConnectionType[] = [
  'salesforce',
  'hubspot',
  'attio',
  'slack',
  'email',
];

export function isConnectionType(value: unknown): value is ConnectionType {
  return CONNECTION_TYPES.some((type) => type === value);
}

/** A connection as a PATCH writes it: only what the agent stores. */
export interface AgentConnectionInput {
  type: ConnectionType;
  /**
   * False: removed from the agent, which then can't use that app at all. A
   * connection never stored reads as on with default policies (a play that
   * uses it still runs).
   */
  enabled: boolean;
  /**
   * Who approves this connection's Ask each time holds and who its writes are
   * logged under in Cleanlist. Null means the agent's owner. It never changes
   * whose CRM login a write uses (the organization's one connection).
   */
  actingUserId: string | null;
  /** A read offers Always allow or Never; an unset write is Always allow. */
  policy: Partial<Record<ToolKey, ToolPolicy>>;
}

/** A member a setting names: first name (else email), and whether they left. */
export interface PersonRef {
  userId: string;
  name: string;
  left: boolean;
}

/**
 * A connection as the API returns it: what the agent stores, and what the API
 * works out around it. The extra fields are read only: the API ignores them in
 * a PATCH, and `connectionInput` leaves them out of every write.
 */
export interface AgentConnection extends AgentConnectionInput {
  /** Who the connection acts as: `actingUserId`, else the agent's owner. */
  actsAs?: PersonRef | null;
  /** `actsAs` is the agent's owner because no one is picked. */
  actsAsOwner?: boolean;
  /** CRM only: the member who connected it, whom the CRM shows as the writer. */
  connectedBy?: PersonRef | null;
  /**
   * An app the agent's plays use that it never stored (an older agent): it
   * reads as on with default policies, and is stored when it is changed.
   */
  fromPlay?: boolean;
}

/**
 * Why a step of one of the agent's plays can't run as the agent's settings
 * stand, or asks someone first: the API's one decision
 * (`providers/agents/tool_policy.py`), in its words. The same issue comes on
 * the agent (`AgentDto.blocked`, `asksFirst`) and from the play's validate
 * route.
 */
export type SettingsIssueCode =
  /** An ability the step uses is off for the agent (`key`). */
  | 'agent_ability_off'
  /** The app the step uses was removed from the agent (`connection`). */
  | 'agent_connection_off'
  /** The tool the step uses is Never on the connection (`key`, `connection`). */
  | 'agent_policy_never'
  /** The connection acts as a member who left (`connection`). */
  | 'agent_acting_user_gone'
  /** A warning: a write set to Ask each time holds its records for `approver`. */
  | 'agent_step_asks';

export interface SettingsIssue {
  playId: string;
  nodeId: string | null;
  code: SettingsIssueCode | (string & {});
  /** The words every surface uses: "Enrich is off for this agent". */
  message: string;
  /** Where the user changes it, in words; the page builds its own one click. */
  fix: string | null;
  /** The ability or connection tool the issue is about (`enrich`, `crm_write`). */
  key: string | null;
  /** The app the issue is about (`salesforce`). */
  connection: string | null;
  /** Who a step that asks first asks. */
  approver: string | null;
}

export interface AgentLimits {
  creditsPerRun: number | null;
  recordsPerRun: number | null;
  /** A brief's budget a run: 1 to 1,000; 100 by default. */
  modelCreditsPerRun: number;
}

export interface AgentDeliveryMessages {
  slackChannelId: string | null;
  slackChannelName: string | null;
  slackDm: boolean;
  email: string[];
}

export interface AgentDeliveryData {
  /** Where the engine stores a play's records: any agent with a play has one. */
  leadListId: string | null;
  /** The list the user picked for the agent's results; null when none. */
  configuredLeadListId?: string | null;
  crmSync: boolean;
}

export interface AgentDelivery {
  messages: AgentDeliveryMessages;
  data: AgentDeliveryData;
}

/** Kinds only an agent without a play carries; nothing runs them yet. */
export type AgentOnlyTriggerKind = 'schedule' | 'one_time';

export type AgentTriggerKind = TriggerKind | AgentOnlyTriggerKind;

export interface TriggerDto {
  id: string;
  /** Null while the play's trigger card has no source yet. */
  kind: AgentTriggerKind | null;
  /** A play trigger's card config without its schedule; free-form on an agent trigger. */
  config: Record<string, unknown>;
  /** Null or absent means every 30 minutes. */
  schedule: TriggerSchedule | null;
  note: string | null;
  enabled: boolean;
  lastFiredAt: string | null;
  nextAt: string | null;
  /** 'play': the play's trigger card. 'agent': stored on the agent itself. */
  source: 'play' | 'agent';
  /** A play trigger's health (`CustomPlaybookDto.triggerHealth`). */
  health?: TriggerHealth | null;
}

/** A trigger as a PATCH writes it; the fire times and health are the server's. */
export type TriggerInput = Omit<
  TriggerDto,
  'lastFiredAt' | 'nextAt' | 'health'
>;

export interface AgentDto {
  id: string;
  organizationId: string;
  ownerUserId: string;
  /**
   * Never from the server: set in the cache with an owner change still out,
   * together with the owner it asks for, and gone with the answer (or with
   * the rollback when refused).
   */
  ownerChangePending?: boolean;
  name: string;
  /**
   * The mark's colour, a palette key (`MARK_COLOURS_BY_KEY`), given when the
   * agent is created and never changed. Null for an agent made before colours
   * were stored, until the backfill gives it one.
   */
  markColor: string | null;
  /** The one line the index shows. */
  description: string | null;
  /**
   * What the agent is for, in one line ("Book 5 meetings a month with tax
   * directors"): Clu writes it when it builds the play, and reads it beside
   * the agent's outcomes. Behind `clu_builder_v2`; absent from an older API.
   */
  goal?: string | null;
  instructions: string | null;
  selfEditInstructions: boolean;
  skillRefs: SkillRef[];
  knowledge: AgentKnowledge;
  /**
   * Every app the agent may use, including those its plays use that it never
   * stored (`fromPlay`), and those removed from it (`enabled: false`).
   */
  connections: AgentConnection[];
  /** Every key present, defaults applied. */
  tools: Record<ToolKey, boolean>;
  triggers: TriggerDto[];
  /** The plays this agent owns, oldest first. */
  playIds: string[];
  limits: AgentLimits;
  /** The window the credit limit counts in across the plays ("Credits per week"; "Credits per 24 hours" for a play with no schedule or no play yet). */
  capWindow?: string | null;
  delivery: AgentDelivery;
  status: AgentStatus;
  aiManagedTriggers: boolean;
  isFavorite: boolean;
  archivedAt: string | null;
  version: number;
  createdAt: string;
  modifiedAt: string;
  /** The first play's next scheduled check. */
  nextRunAt: string | null;
  lastRunAt: string | null;
  /** The play proposal Clu made that the user has not reviewed yet. */
  pendingProposalId: string | null;
  /** Null until Clu has greeted the user in this agent's workspace. */
  greetedAt: string | null;
  /** Null until Clu (or the user, by hand) has finished setting the agent up. */
  setupCompletedAt: string | null;
  /**
   * The steps of the agent's plays its settings keep from running, first one
   * first (the Blocked state shows its message). The fields below are
   * absent from an API that predates the check; read them through
   * `agent-enforcement.util`.
   */
  blocked?: SettingsIssue[];
  /** The steps that hold their records for someone's go ahead first. */
  asksFirst?: SettingsIssue[];
  /** The abilities the agent's plays use ("In use", or "Needed" while off). */
  abilitiesInUse?: ToolKey[];
  /** The apps the agent's plays use, stored on the agent or not. */
  playConnectionTypes?: ConnectionType[];
  /**
   * What keeps each app's tools from running now, by app and tool, in the
   * validate route's codes: every app, listed on the agent or not.
   */
  appBlocks?: Partial<Record<ConnectionType, Partial<Record<ToolKey, string>>>>;
  /**
   * What the agent's saved plays don't do yet, decided by the server
   * (`play_completeness`, CLE-478); on the single-agent reads only (GET,
   * Accept), absent or empty in lists.
   */
  gaps?: PlayGap[];
  /** Up to three replies that ask Clu to propose the gaps' fixes. */
  quickReplies?: string[];
  /** What the build kit built the play from; null when Clu didn't build it. */
  builtFrom?: BuiltFrom | null;
}

/** A gap's code (`play_completeness.GAP_ORDER`). */
export type PlayGapCode =
  | 'agent_off'
  | 'crm_sync_off'
  | 'crm_mapping_missing'
  | 'no_destination'
  | 'found_fields_unused'
  | 'notify_counts_only'
  | 'unattended_silent'
  | 'branch_arm_idle';

/** Why CRM sync is off. */
export type CrmSyncOffReason =
  | 'not_added'
  | 'not_connected'
  | 'disconnected'
  | 'declined';

/** What one of the agent's saved plays doesn't do yet. */
export interface PlayGap {
  code: PlayGapCode | string;
  /** Clu's line about it. */
  message: string;
  /** crm_sync_off only: why it is off. */
  reason?: CrmSyncOffReason | string | null;
  /** The reply that asks Clu to propose the fix; none once declined. */
  quickReply?: string | null;
  /** Set when the agent has several plays. */
  playId?: string | null;
}

/** What the build kit built the agent's play from. */
export interface BuiltFrom {
  kind: 'recipe' | 'skill' | 'none' | string;
  name?: string | null;
  slug?: string | null;
}

/** Runs per UTC day, oldest first, one entry per day of `AgentsActivityDto.days`. */
export interface ActivitySeries {
  runs: number[];
  lastRunAt: string | null;
}

/**
 * `GET /gtm-agents/activity?days=14`: the organization's play runs (dry runs
 * excluded) per UTC day, by play and by agent (an agent's series is the sum
 * of its plays').
 */
export interface AgentsActivityDto {
  /** 'YYYY-MM-DD', oldest first. */
  days: string[];
  plays: Record<string, ActivitySeries>;
  agents: Record<string, ActivitySeries>;
}

export interface SkillUsedBy {
  agentId: string;
  name: string;
}

export interface SkillVersionDto {
  version: number;
  name: string;
  description: string;
  authorKind: 'user' | 'clu';
  authorId: string;
  createdAt: string;
}

export interface SkillDto {
  /** Agent-local record skills; null on the shared library and built-ins. */
  agentId?: string | null;
  /** Draft creations in the current proposal cannot be attached or executed. */
  status?: 'draft' | 'live' | null;
  /** A live skill whose edit waits in the current proposal. The accepted
   * version still runs, so it stays attachable and selectable. */
  pendingUpdate?: boolean;
  outputs?: RecordSkillOutput[];
  /** Write only: each kind it writes and that kind's outputs. */
  kinds?: { kind: string; label: string; outputs: RecordSkillOutput[] }[];
  id: string;
  kind: SkillKind;
  name: string;
  description: string | null;
  /** Procedure skills only. */
  instructions?: string | null;
  /** Procedure skills only: the play that carries the deterministic part. */
  playbookId?: string | null;
  /** Null on the built in record skills, which have no history. */
  version: number | null;
  createdByKind: 'user' | 'clu' | null;
  createdById: string | null;
  usedBy: SkillUsedBy[];
  createdAt: string | null;
  modifiedAt: string | null;
  /** `GET /skills/{id}` of a procedure skill only. */
  versions?: SkillVersionDto[];
  /** A procedure skill made for Clu writing; an older API sends none. */
  forWriting?: boolean;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface CreateAgentBody {
  name?: string;
  description?: string;
  /** The draft's id, picked by the page so a lost answer can be resent. */
  id?: string;
}

/** A partial update: every field optional, each one sent whole. */
export interface PatchAgentBody {
  /** The owned play a trigger edit targets. */
  playbookId?: string;
  holds?: HoldChoice;
  name?: string;
  description?: string;
  /** Empty clears it. */
  goal?: string;
  instructions?: string;
  selfEditInstructions?: boolean;
  skillRefs?: SkillRefInput[];
  knowledge?: AgentKnowledge;
  /** The stored connections, whole: read only fields are never sent. */
  connections?: AgentConnectionInput[];
  tools?: Partial<Record<ToolKey, boolean>>;
  /** Exactly one entry for now. */
  triggers?: TriggerInput[];
  /**
   * Every limit, always together: the server stores the field whole, so one
   * left out (the model budget) would go back to its default.
   */
  limits?: AgentLimits;
  delivery?: AgentDelivery;
  aiManagedTriggers?: boolean;
  isFavorite?: boolean;
  /** Only `true`: finishes the setup (the user sets the agent up by hand). */
  setupCompleted?: true;
}

export interface SetStatusBody {
  holds?: HoldChoice;
  status: 'active' | 'paused';
  /** Turn on without a Try (409 `try_approval_required` otherwise). */
  skipTry?: true;
}

/**
 * An app the agent uses that won't reach its new owner (no HubSpot or
 * Salesforce user mapped to them, their Slack not linked), or a list a step
 * reads that isn't shared with them (`name` is the list's).
 */
export type MissingConnectionDto =
  | { type: ConnectionType; reason: 'no_crm_user' | 'slack_not_linked' }
  | { type: 'list'; reason: 'not_shared'; name: string };

/** A private list of the person changing the owner, shared with the new one. */
export interface SharedListDto {
  id: string;
  name: string;
}

/** `POST /gtm-agents/{id}/owner`'s answer. */
export interface OwnerChangeDto {
  agent: AgentDto;
  missingConnections: MissingConnectionDto[];
  sharedLists?: SharedListDto[];
}

/** No `playbookId` creates a new play for the agent. */
export interface AttachPlayBody {
  playbookId?: string;
}

export interface CreateSkillBody {
  name: string;
  description: string;
  instructions: string;
  playbookId?: string;
  forWriting?: boolean;
}

export interface PatchSkillBody {
  name?: string;
  description?: string;
  instructions?: string;
  playbookId?: string | null;
  forWriting?: boolean;
}

// ---------------------------------------------------------------------------
// Play proposals: a change Clu proposes to an agent's play, reviewed on the
// canvas. `GET /gtm-agents/{id}/play-proposals/{proposalId}`.
// ---------------------------------------------------------------------------

export type PlayProposalStatus =
  | 'pending'
  | 'accepted'
  | 'discarded'
  | 'superseded';

export interface ProposalNodeDto {
  id: string;
  type: string;
  /** The step's label from the step catalog ("Enrich", "Agents"). */
  headline: string;
}

export interface PlayProposalIssue {
  severity: 'error' | 'warning';
  blocks?: boolean;
  nodeId: string | null;
  code: string;
  message: string;
}

export interface PlayProposalRequirement {
  id: string;
  text: string;
  state: 'inferred_explicit' | 'user_confirmed';
  coverage: 'supported' | 'unverified';
}

export interface ReviewSummaryRow {
  label: string;
  state: string;
}

/** Compiled by the API from this proposal's graph and effective run limits. */
export interface PlayProposalPreview {
  graphHash: string;
  trigger: {
    summary: string;
    details: readonly { label: string; value: string }[];
  };
  steps: readonly {
    id: string;
    type: string;
    headline: string;
    summary: string | null;
    details: readonly { label: string; value: string }[];
  }[];
  credits: {
    perRecordMax: number | null;
    perCompanyMax: number | null;
    perRunMax: number | null;
    capPerRun: number | null;
    /** The window the credit cap counts in ("Credits per 24 hours"); missing on older APIs. */
    capWindow?: string | null;
    recordsPerRun: number | null;
    /** The full volume's week (perRunMax a run); missing on older APIs. */
    perWeekMax?: number | null;
    runsPerWeek?: number | null;
    /** "month" or "quarter" for a play that runs once a period. */
    period?: string | null;
    /** The most a run and a week charge under the credit limit. */
    cappedPerRunMax?: number | null;
    cappedPerWeekMax?: number | null;
  };
  warnings: readonly string[];
}

/** Derived review state; optional while older proposal responses remain in cache. */
export interface PlayProposalReview {
  buildState?: 'ready' | 'blocked' | null;
  canAccept?: boolean;
  requirementRevision?: string | null;
  requirements?: readonly PlayProposalRequirement[];
  blockedReasons?: readonly {
    code: string;
    message: string;
    nodeId?: string | null;
  }[];
}

/**
 * Why a step is on a proposal's review card. `errors`: only because its
 * failed records now carry on past it (`onError`).
 */
export type ReviewCardReason = 'credits' | 'outside' | 'errors';

/**
 * What an item raises against the saved play. `key`: `perPeriod` (with
 * `per`, the period a run's records count in), `schedule`, `scope`,
 * `backfill`, `maxPeoplePerAccount`, `skipInCrm`, `onError`, `retries`,
 * `retryUnconfirmed`, `writeSafety`, `assignment` (with `fields` for update
 * fields filled by round robin), `writeMode` (with `fields`),
 * `syncProfileId`, and the run limits' `recordsPerRun`,
 * `creditsPerRun` and `modelCreditsPerRun` (`now` null: no limit).
 */
export interface ReviewCardChange {
  key: string;
  was: unknown;
  now: unknown;
  per?: string;
  fields?: string[];
}

export interface ReviewCardItem {
  nodeId: string;
  /** The step's type, or `limits` for the agent's run limits. */
  type: string;
  /** The step's headline ("Enrich · email, mobile"). */
  label: string;
  reason: ReviewCardReason;
  /** A step the play doesn't have yet (else one whose settings change). */
  new: boolean;
  /** Its most per record (or per run, for a brief), when it has a price. */
  credits?: number;
  /** What it raises (missing on older APIs and for nothing raised). */
  changes?: ReviewCardChange[];
}

/** The most a run and a week charge under the agent's credit limit. */
export interface ReviewCardTotals {
  perRunMax: number | null;
  perWeekMax: number | null;
  /** "month" or "quarter" for a play that runs once a period. */
  period?: string | null;
}

export interface ReviewCard {
  items: ReviewCardItem[];
  digest: string;
  /** Missing on older APIs and on a one-off's card. */
  totals?: ReviewCardTotals | null;
}

export interface PlayProposalDto extends PlayProposalReview {
  id: string;
  /** Null creates a new play on Accept. Missing is a legacy single-play DTO. */
  playbookId?: string | null;
  status: PlayProposalStatus;
  summary: string;
  /** The play's version the proposal was made against. */
  baseVersion: number | null;
  /** The whole proposed graph, in the builder's `CustomGraph` shape. */
  graph: Record<string, unknown>;
  preview?: PlayProposalPreview | null;
  /** The run limits the product defaulted ("creditsPerRun", "recordsPerRun"). */
  defaultedLimits?: string[];
  /**
   * True only when the API's candidate test covers this proposal's trigger.
   * Missing on an API that has no candidate test.
   */
  testable?: boolean;
  issues: PlayProposalIssue[];
  holds_affected?: Record<string, number>;
  /** Ids of the steps the proposal adds. */
  added: string[];
  /** Ids of the steps the proposal changes. */
  changed: string[];
  removed: ProposalNodeDto[];
  createdAt: string | null;
  /**
   * Agents: the Sync steps whose default field mapping leaves out what the
   * proposal's Enrich steps find (`crm_sync_unmapped_fields`, with `fields`),
   * read against the org's mapping now. Pending proposals only.
   */
  mappingWarnings?: GraphIssue[];
  /** The chat Clu proposed it in, where Accept and Discard post a line. */
  conversationId?: string | null;
  /**
   * The consent split (`clu_builder_v2`): the steps it adds or changes
   * that spend credits or reach outside the account, which the user confirms
   * once on Accept by sending back `digest`. Null for none, or off.
   */
  reviewCard?: ReviewCard | null;
  /**
   * What Accept changes on the agent besides its play. `tools` only ever
   * turns abilities on ("Turns on Enrich"): Clu can turn one off by itself.
   */
  agentChanges?:
    | ({
        tools?: Partial<Record<ToolKey, boolean>>;
      } & Record<string, unknown>)
    | null;
}

/**
 * `POST /gtm-agents/{id}/play-proposals/{proposalId}/accept`: the agent, and
 * for a cleanup play how many approved merges the new rules sent back to
 * review (W5 design 2.7); 0 or absent for none.
 */
export type AcceptPlayProposalResponse = AgentDto & { reopenedMerges?: number };

/** Read-only evidence from the exact pending proposal, using fictional inputs. */
export interface CandidateTestDto {
  id: string;
  proposalId: string;
  status: string;
  source: 'fixture';
  stale: boolean;
  passed: boolean;
  limitations: string[];
  /** `job-change-mixed` or `company-signal-firms`. */
  fixtureSet?: string;
  /** The proposal's requirements, `supported` where this check covered them. */
  requirements?: PlayProposalRequirement[];
  requirementSummary?: ReviewSummaryRow[];
  /** A template's build rules the fixture records broke. */
  diagnostics?: { code?: string; nodeId?: string | null; message?: string }[];
  creditsUsed: number;
  binding: { requirement_revision?: string; [key: string]: unknown };
  errors: { code?: string; message?: string }[];
  records: {
    id: string;
    fixture: string;
    kind?: 'person' | 'company';
    status: string;
    nodeId: string | null;
    error: { code?: string; message?: string } | string | null;
    plans: Record<string, Record<string, unknown> | null>;
  }[];
  nodes: {
    nodeId: string;
    status: string;
    entered: number;
    succeeded: number;
    failed: number;
    filtered: number;
    detail: Record<string, unknown>;
  }[];
}

/** Where the CRM scan behind an agent's cleanup play stands. */
export type CrmScanState = 'ready' | 'scanning' | 'queued' | 'idle' | 'failed';

/**
 * `GET /gtm-agents/{id}/crm-scan` (W5 design 3.2): the scan a cleanup play
 * reads its duplicate groups from. `idle` for an agent whose play isn't a
 * cleanup play.
 */
export interface CrmScanStatusDto {
  state: CrmScanState;
  crmType: string | null;
  /**
   * Records read so far while scanning; the scan's size once ready, and the
   * last finished scan's when idle.
   */
  recordsIndexed: number | null;
  /**
   * When the scan finished. Idle with one set: a finished scan the next run
   * scans again first (`reason` says why).
   */
  completedAt: string | null;
  phase: string | null;
  estimateMinutes: number | null;
  /** A Run now or Run Test waiting for the scan to finish. */
  pendingRun: Record<string, unknown> | null;
  /**
   * Why a scan failed (`crm_not_connected`, or the last scan error), or why
   * an idle play's finished scan is scanned again (`merged_since`,
   * `play_changed`).
   */
  reason: string | null;
  /** Objects the play checks that the scan could not read ("leads"). */
  unavailable: string[];
}

export interface TryBudgetSetting {
  budgetCredits: number;
}

/** The server's agent templates (cleanse-api `play_templates.py`
 *  `TEMPLATES` and `AGENT_TEMPLATES`), by the key a play proposal uses. */
export type AgentTemplateKey =
  | 'weekly_icp_outbound'
  | 'inbound_route_back'
  | 'meeting_brief'
  | 'call_follow_up_fireflies'
  | 'call_follow_up_gong'
  | 'call_follow_up_fathom'
  | 'hiring_signal'
  | 'new_in_role'
  | 'funding_round'
  | 'store_openings'
  | 'champion_job_change'
  | 'lookalikes';

/** GET /gtm-agents/templates/{key}/graph: the template's graph as the server
 *  builds it, with fresh ids on every read. */
export interface AgentTemplateGraphDto {
  key: AgentTemplateKey;
  graph: CustomGraph;
}
